import React from "react";
import { Box, useTheme } from "@mui/material";
import { Outlet } from "react-router-dom";
import { MainNavbar } from "../MainNavbar/MainNavbar";
import { Footer } from "../Footer/Footer";
import { styled } from "@mui/material/styles";
import { ScrollProgress } from "../ScrollProgress/ScrollProgress";

const StyledMainContent = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",

  // Global scrollbar styles
  "@global": {
    // Firefox
    "*": {
      scrollbarWidth: "thin",
      scrollbarColor:
        theme.palette.mode === "dark"
          ? "rgba(6, 182, 212, 0.3) rgba(255, 255, 255, 0.05)"
          : "rgba(8, 145, 178, 0.3) rgba(0, 0, 0, 0.05)",
    },

    // Chrome, Edge, Safari
    "*::-webkit-scrollbar": {
      width: "8px",
      background: "transparent",
    },

    "*::-webkit-scrollbar-track": {
      background:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.05)",
      borderRadius: "100vh",
      margin: "4px",
    },

    "*::-webkit-scrollbar-thumb": {
      background:
        theme.palette.mode === "dark"
          ? "linear-gradient(180deg, rgba(6, 182, 212, 0.3) 0%, rgba(6, 182, 212, 0.4) 100%)"
          : "linear-gradient(180deg, rgba(8, 145, 178, 0.3) 0%, rgba(8, 145, 178, 0.4) 100%)",
      border: "2px solid transparent",
      backgroundClip: "padding-box",
      borderRadius: "100vh",
      minHeight: "48px",
      transition: "all 200ms ease-in-out",

      "&:hover": {
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(6, 182, 212, 0.5) 0%, rgba(6, 182, 212, 0.6) 100%)"
            : "linear-gradient(180deg, rgba(8, 145, 178, 0.5) 0%, rgba(8, 145, 178, 0.6) 100%)",
        cursor: "pointer",
      },

      "&:active": {
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, #06B6D4 0%, rgba(6, 182, 212, 0.8) 100%)"
            : "linear-gradient(180deg, #0891B2 0%, rgba(8, 145, 178, 0.8) 100%)",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 0 10px rgba(6, 182, 212, 0.3)"
            : "0 0 10px rgba(8, 145, 178, 0.3)",
      },
    },

    // Hide scrollbar when inactive but keep functionality
    "*::-webkit-scrollbar-thumb:vertical": {
      opacity: 0,
      transition: "opacity 200ms ease-in-out",
    },

    // Show scrollbar on hover/scroll
    "*:hover::-webkit-scrollbar-thumb:vertical": {
      opacity: 1,
    },

    // Enable smooth scrolling
    "html, body": {
      scrollBehavior: "smooth",
    },
  },
}));

export const MainLayout: React.FC = () => {
  const theme = useTheme();

  return (
    <StyledMainContent>
      <ScrollProgress />
      <MainNavbar />
      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>
      <Footer />
    </StyledMainContent>
  );
};
